import { lazy } from 'react';
import { createBrowserRouter } from 'react-router';
import type { RouteObject } from 'react-router';

import { AppShell } from '../components/layout/app-shell';
import { RouteErrorPage } from '../components/common/route-error-page';
import { NOT_FOUND_DETAIL, StatusPage } from '../components/common/status-page';
import { HomeRedirect, RequireAdminArea, RequireAuth, RequireCapability } from './route-guards';
import { ROUTES } from '../lib/routes';
import { LoginPage } from '../pages/login-page';

/**
 * Route tree.
 *
 * The sign-in screen is in the entry bundle because it is the first paint for every
 * signed-out visitor, and because Microsoft redirects back to it. Every
 * authenticated screen is code-split with `React.lazy`; the `Suspense` boundary
 * lives once in `AppShell`, and because router navigations run as transitions the
 * current screen stays visible while the next chunk loads instead of flashing a
 * fallback.
 *
 * `RequireAuth` and `RequireAdminArea` render an `<Outlet/>`, so they sit on their own
 * level and gate the level below. They are UX only — see `route-guards.tsx`.
 *
 * Error boundaries: the pathless route under `AppShell` catches failures of any
 * single screen and renders them inside the shell, so navigation stays usable. The
 * outer boundaries catch failures in the shell itself and in sign-in.
 */
const ProjectsPage = lazy(() =>
  import('../pages/projects-page').then((module) => ({ default: module.ProjectsPage })),
);
const ProjectDetailPage = lazy(() =>
  import('../pages/project-detail-page').then((module) => ({ default: module.ProjectDetailPage })),
);
const AccessLogsPage = lazy(() =>
  import('../pages/access-logs-page').then((module) => ({ default: module.AccessLogsPage })),
);
const ProfilePage = lazy(() =>
  import('../pages/profile-page').then((module) => ({ default: module.ProfilePage })),
);
const AdminDashboardPage = lazy(() =>
  import('../pages/admin/admin-dashboard-page').then((module) => ({
    default: module.AdminDashboardPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import('../pages/admin/admin-users-page').then((module) => ({ default: module.AdminUsersPage })),
);
const AdminUserCreatePage = lazy(() =>
  import('../pages/admin/admin-user-create-page').then((module) => ({
    default: module.AdminUserCreatePage,
  })),
);
const AdminUserDetailPage = lazy(() =>
  import('../pages/admin/admin-user-detail-page').then((module) => ({
    default: module.AdminUserDetailPage,
  })),
);
const AdminUserEditPage = lazy(() =>
  import('../pages/admin/admin-user-edit-page').then((module) => ({
    default: module.AdminUserEditPage,
  })),
);
const AdminProjectsPage = lazy(() =>
  import('../pages/admin/admin-projects-page').then((module) => ({
    default: module.AdminProjectsPage,
  })),
);
const AdminProjectCreatePage = lazy(() =>
  import('../pages/admin/admin-project-create-page').then((module) => ({
    default: module.AdminProjectCreatePage,
  })),
);
const AdminProjectDetailPage = lazy(() =>
  import('../pages/admin/admin-project-detail-page').then((module) => ({
    default: module.AdminProjectDetailPage,
  })),
);
const AdminProjectEditPage = lazy(() =>
  import('../pages/admin/admin-project-edit-page').then((module) => ({
    default: module.AdminProjectEditPage,
  })),
);
const AdminPermissionsPage = lazy(() =>
  import('../pages/admin/admin-permissions-page').then((module) => ({
    default: module.AdminPermissionsPage,
  })),
);
const AdminAccessLogsPage = lazy(() =>
  import('../pages/admin/admin-access-logs-page').then((module) => ({
    default: module.AdminAccessLogsPage,
  })),
);

const AdminAnalyticsPage = lazy(() =>
  import('../pages/admin/admin-analytics-page').then((module) => ({
    default: module.AdminAnalyticsPage,
  })),
);

export const routes: RouteObject[] = [
  {
    path: ROUTES.login,
    element: <LoginPage />,
    errorElement: <RouteErrorPage variant="page" />,
  },

  {
    element: <RequireAuth />,
    errorElement: <RouteErrorPage variant="page" />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            errorElement: <RouteErrorPage variant="inline" />,
            children: [
              { index: true, element: <HomeRedirect /> },
              { path: 'projects', element: <ProjectsPage /> },
              { path: 'projects/:projectId', element: <ProjectDetailPage /> },
              { path: 'access-logs', element: <AccessLogsPage /> },
              { path: 'profile', element: <ProfilePage /> },

              {
                path: 'admin',
                element: <RequireAdminArea />,
                children: [
                  { index: true, element: <AdminDashboardPage /> },
                  {
                    path: 'users',
                    element: <RequireCapability capability="users:manage" />,
                    children: [
                      { index: true, element: <AdminUsersPage /> },
                      // Static segment, so it ranks above `:id`.
                      { path: 'create', element: <AdminUserCreatePage /> },
                      { path: ':id', element: <AdminUserDetailPage /> },
                      { path: ':id/edit', element: <AdminUserEditPage /> },
                    ],
                  },
                  {
                    path: 'projects',
                    element: <RequireCapability capability="projects:manage" />,
                    children: [
                      { index: true, element: <AdminProjectsPage /> },
                      // Static segment, so it ranks above `:id`.
                      { path: 'create', element: <AdminProjectCreatePage /> },
                      { path: ':id', element: <AdminProjectDetailPage /> },
                      { path: ':id/edit', element: <AdminProjectEditPage /> },
                    ],
                  },
                  {
                    path: 'permissions',
                    element: <RequireCapability capability="grants:manage" />,
                    children: [{ index: true, element: <AdminPermissionsPage /> }],
                  },
                  {
                    path: 'access-logs',
                    element: <RequireCapability capability="access-logs:read" />,
                    children: [{ index: true, element: <AdminAccessLogsPage /> }],
                  },
                  {
                    path: 'analytics',
                    element: <RequireCapability capability="access-logs:read" />,
                    children: [{ index: true, element: <AdminAnalyticsPage /> }],
                  },
                ],
              },

              { path: '*', element: <StatusPage code="404" detail={NOT_FOUND_DETAIL} /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
